import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangleIcon, XIcon, ArrowRightIcon, ZapIcon } from 'lucide-react';

// Sticky, attention-grabbing banner shown on Solo trial when ≤ 3 days remain.
// Color escalates: 3 days → amber, 2 days → orange, ≤1 day → red.
// Dismissable per browser-tab session (sessionStorage); banner returns on
// page refresh / new session so users can't permanently miss the expiry.
//
// Renders nothing if:
// - user has no trial_info or is_trial=false
// - plan is not 'solo' (banner is Solo-specific copy)
// - days_remaining > 3
// - banner was dismissed this session
const SoloTrialExpiryBanner = ({ user }) => {
  const trial = user?.trial_info;
  const plan = trial?.plan || user?.company?.subscription_plan;
  const days = trial?.days_remaining;

  // Cache key tied to the current trial_end so a re-trial would reset dismissal
  const cacheKey = `solo_trial_banner_dismissed_${trial?.trial_end || ''}`;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(cacheKey) === '1';
    } catch {
      return false;
    }
  });

  // Reset dismissal if a new trial_end is detected
  useEffect(() => {
    setDismissed(sessionStorage.getItem(cacheKey) === '1');
  }, [cacheKey]);

  if (!trial?.is_trial || plan !== 'solo') return null;
  if (typeof days !== 'number' || days > 3 || days < 0) return null;
  if (dismissed) return null;

  // Tone based on urgency
  const tone =
    days <= 1
      ? {
          bg: 'bg-red-50',
          border: 'border-red-300',
          text: 'text-red-800',
          icon: 'text-red-500',
          btn: 'bg-red-600 hover:bg-red-700',
          dot: 'bg-red-500',
        }
      : days === 2
        ? {
            bg: 'bg-orange-50',
            border: 'border-orange-300',
            text: 'text-orange-800',
            icon: 'text-orange-500',
            btn: 'bg-orange-600 hover:bg-orange-700',
            dot: 'bg-orange-500',
          }
        : {
            bg: 'bg-amber-50',
            border: 'border-amber-300',
            text: 'text-amber-800',
            icon: 'text-amber-500',
            btn: 'bg-amber-600 hover:bg-amber-700',
            dot: 'bg-amber-500',
          };

  // Headline copy
  const headline =
    days === 0
      ? 'Solo deneme süreniz bugün sona eriyor!'
      : days === 1
        ? 'Solo deneme süreniz yarın sona eriyor!'
        : `Solo deneme süreniz ${days} gün içinde sona eriyor`;

  const dismiss = () => {
    try {
      sessionStorage.setItem(cacheKey, '1');
    } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className={`relative ${tone.bg} ${tone.border} border-b px-4 py-2.5 flex items-center justify-between gap-3 text-sm`}
      data-testid="solo-trial-expiry-banner"
      data-days-remaining={days}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="relative flex h-2 w-2 flex-shrink-0">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${tone.dot} opacity-75`} />
          <span className={`relative inline-flex rounded-full h-2 w-2 ${tone.dot}`} />
        </span>
        <AlertTriangleIcon className={`w-4 h-4 flex-shrink-0 ${tone.icon}`} />
        <div className={`min-w-0 ${tone.text}`}>
          <span className="font-semibold">{headline}</span>
          <span className="hidden md:inline mx-2 opacity-60">·</span>
          <span className="hidden md:inline opacity-90">
            Kaldığınız yerden devam etmek için Solo planına geçin — yalnızca $15/ay.
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          to="/subscription"
          className={`${tone.btn} text-white px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1 transition-colors`}
          data-testid="solo-trial-banner-upgrade"
        >
          <ZapIcon className="w-3.5 h-3.5" />
          Şimdi yükselt
          <ArrowRightIcon className="w-3 h-3" />
        </Link>
        <button
          type="button"
          onClick={dismiss}
          className={`p-1 rounded-md ${tone.text} hover:bg-black/5 transition-colors`}
          aria-label="Banner'ı bu oturum için gizle"
          data-testid="solo-trial-banner-dismiss"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default SoloTrialExpiryBanner;
